import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreditCard, Check, Sparkles, Clock, AlertTriangle, FileText } from 'lucide-react';
import {
  getSubscriptionStatus, getSubscriptionPackages, getDoctorPayments,
} from '../redux/subscriptionSlice';
import { ACCESS_CONTROLS, BILLING_PERIODS, PACKAGE_ACCENTS } from '../data/accessControls';
import SubscriptionCheckoutModal from '../components/modals/SubscriptionCheckoutModal';
import PackageCardView from '../components/subscription/PackageCardView';
import InvoiceModal from '../components/subscription/InvoiceModal';
import ContactModal from '../components/subscription/ContactModal';

const STATUS_BADGE = {
  trial: { label: 'Trial', cls: 'bg-blue-100 text-blue-700' },
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' },
  grace: { label: 'Grace period', cls: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-700' },
};

const priceLabel = (p) => {
  if (!p || p.price == null || String(p.price) === '') return 'On request';
  return String(p.price) === '0' ? 'Free' : `€${p.price}`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

// Lowest enabled period price → "from €X"; falls back to the legacy single price.
// A custom (non-purchasable) plan shows "Custom pricing" instead of a number.
const cardPrice = (p) => {
  if (p.purchasable === false) return 'Custom pricing';
  const bp = p.billingPeriods || {};
  const prices = BILLING_PERIODS.filter((x) => bp[x.id] && bp[x.id].enabled)
    .map((x) => Number(bp[x.id].price)).filter((n) => Number.isFinite(n));
  return prices.length ? `from €${Math.min(...prices)}` : priceLabel(p);
};
const periodHint = (p) => {
  const bp = p.billingPeriods || {};
  const labels = BILLING_PERIODS.filter((x) => bp[x.id] && bp[x.id].enabled).map((x) => x.label);
  return labels.length ? labels.join(' · ') : `${p.durationDays || 30} days`;
};

const MySubscription = () => {
  const dispatch = useDispatch();
  const doctorId = localStorage.getItem('ID');
  const { mySubscription: sub, statusLoading, packages, payments, loading } = useSelector((s) => s.subscription);
  const [checkoutPkg, setCheckoutPkg] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [contactPkg, setContactPkg] = useState(null);

  useEffect(() => {
    if (doctorId) {
      dispatch(getSubscriptionStatus(doctorId));
      dispatch(getDoctorPayments(doctorId));
    }
    // Always refetch so the plan cards + checkout modal have current billing
    // periods/prices (a stale list would send no period and 400 at checkout).
    dispatch(getSubscriptionPackages());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  const badge = STATUS_BADGE[sub?.status] || STATUS_BADGE.active;
  const expired = sub?.status === 'expired';

  const included = ACCESS_CONTROLS.filter((c) => {
    const v = sub?.controls?.[c.id];
    return v !== undefined && v !== false && v !== '';
  }).map((c) => ({ id: c.id, label: c.label, value: c.type === 'value' ? String(sub.controls[c.id]) : null }));

  // Plans to switch/upgrade to = active, non-default, and NOT the current plan
  // (you renew the current one from the Current plan card, not this list).
  const buyable = packages.filter((p) => p.isActive !== false && !p.isDefault && p.id !== sub?.packageId);

  // The current plan's package (for the Renew button). Renew is offered only for a
  // renewable paid plan in its last stretch (<= 10 days left) — not the Free trial.
  const currentPkg = packages.find((p) => p.id === sub?.packageId) || null;
  const canRenew = !!currentPkg && currentPkg.purchasable !== false && !sub?.isFree && (sub?.daysLeft ?? 99) <= 10;

  // Admin set a pay-to-activate change — the doctor is locked until they pay it.
  const pendingPay = sub?.paymentRequired ? sub.pending : null;
  const pendingPkg = pendingPay ? (packages.find((p) => p.id === pendingPay.packageId) || null) : null;

  const refresh = () => {
    if (doctorId) {
      dispatch(getSubscriptionStatus(doctorId));
      dispatch(getDoctorPayments(doctorId));
    }
  };

  return (
    <div className="w-full p-2 sm:p-4" data-testid="my-subscription-page">
      <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-1">
        My Subscription
      </h1>
      <p className="text-sm text-gray-500 mb-6">Your current plan, what's included, and how to renew or upgrade.</p>

      {statusLoading && !sub ? (
        <div className="py-10 flex justify-center">
          <div className="h-7 w-7 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6" data-testid="my-subscription-content">
          {/* 0. Compulsory payment — admin set a new plan that must be paid to activate */}
          {pendingPay && (
            <div className="bg-gradient-to-r from-[#4ABDBD]/10 to-[#3A9B9B]/10 border-2 border-[#4ABDBD] rounded-2xl p-5 sm:p-6" data-testid="pending-payment-card">
              <div className="flex items-start gap-3 flex-wrap">
                <span className="p-2.5 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] text-white shrink-0"><CreditCard className="w-5 h-5" /></span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-[#1a365d]">Action required — pay to activate your new plan</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Your administrator set you to <strong>{pendingPay.packageName}</strong>{pendingPay.periodLabel ? ` (${pendingPay.periodLabel})` : ''}. You can&apos;t continue until this is paid.
                  </p>
                  <div className="mt-3 inline-flex flex-col gap-1 text-sm">
                    <div className="flex items-center justify-between gap-8"><span className="text-slate-500">Plan price</span><span className="font-semibold text-slate-700">€{Number(pendingPay.amount).toLocaleString()}</span></div>
                    {pendingPay.credit > 0 && (
                      <div className="flex items-center justify-between gap-8 text-emerald-600"><span>Credit · unused days</span><span>−€{Number(pendingPay.credit).toLocaleString()}</span></div>
                    )}
                    <div className="flex items-center justify-between gap-8 pt-1 border-t border-[#4ABDBD]/30"><span className="font-semibold text-[#1a365d]">Total due</span><span className="font-bold text-[#1a365d]">€{Number(pendingPay.due).toLocaleString()}</span></div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => pendingPkg && setCheckoutPkg(pendingPkg)}
                  disabled={!pendingPkg}
                  data-testid="pay-pending"
                  className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg transition-all shrink-0 disabled:opacity-50"
                >
                  Pay €{Number(pendingPay.due).toLocaleString()}
                </button>
              </div>
            </div>
          )}

          {/* 1. Current plan */}
          <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6" data-testid="current-plan-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="p-2 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] text-white">
                  <CreditCard className="w-4 h-4" />
                </span>
                <h3 className="text-base sm:text-lg font-bold text-[#1a365d]">Current plan</h3>
              </div>
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {sub?.packageName || '—'}
                  </span>
                  <span className="text-sm text-slate-500">{priceLabel(sub)}</span>
                </div>

                {/* Days left / expiry line */}
                <div className={`flex items-center gap-2 text-sm rounded-xl px-3 py-2 ${expired ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-600'}`}>
                  {expired ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                  {expired
                    ? <span>Expired on {fmtDate(sub?.endDate)} — renew to continue.</span>
                    : <span><strong>{sub?.daysLeft ?? 0}</strong> day{(sub?.daysLeft === 1) ? '' : 's'} left · ends {fmtDate(sub?.graceUntil && sub?.status === 'grace' ? sub.graceUntil : sub?.endDate)}</span>}
                </div>

                {/* Upgrade credit hint */}
                {Number(sub?.remainingValue) > 0 && (
                  <p className="text-xs text-emerald-600 mt-2" data-testid="remaining-value-hint">
                    €{Number(sub.remainingValue).toLocaleString()} unused value — credited toward your next plan if you upgrade now.
                  </p>
                )}

                {/* Renew — only in the last stretch of a paid plan (<= 10 days left) */}
                {canRenew && (
                  <button
                    type="button"
                    onClick={() => setCheckoutPkg(currentPkg)}
                    data-testid="current-plan-renew"
                    className="mt-3 inline-flex items-center justify-center px-5 py-2.5 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg transition-all"
                  >
                    Renew plan
                  </button>
                )}
              </div>

              <div>
                {included.length > 0 ? (
                  <>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#4ABDBD]" /> What's included
                    </p>
                    <ul className="space-y-1.5">
                      {included.map((f) => (
                        <li key={f.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>{f.label}{f.value ? `: ${f.value}` : ''}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">No plan features available.</p>
                )}
              </div>
            </div>
          </div>

          {/* 2. Plans to choose / upgrade / renew — hidden while a pending admin
              change must be paid (the doctor can only pay that assigned plan). */}
          {!pendingPay && (
          <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold text-[#1a365d] mb-1">
              {expired ? 'Renew or upgrade to continue' : 'Switch or renew your plan'}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              {sub?.freeUsed
                ? 'Your free trial has been used — choose a paid plan to continue.'
                : 'Pick a paid plan whenever you’re ready to upgrade.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {buyable.map((p) => {
                const isCurrent = p.id === sub?.packageId;
                const isCustom = p.purchasable === false;
                const a = PACKAGE_ACCENTS[p.accent] || PACKAGE_ACCENTS.teal;
                const renderPrice = (onAccent) => (
                  <div className="mt-1">
                    <span className={`${isCustom ? 'text-xl' : 'text-2xl'} font-black ${onAccent ? 'text-white' : a.text}`}>{cardPrice(p)}</span>
                    <p className={`text-xs mt-0.5 ${onAccent ? 'text-white/80' : 'text-slate-400'}`}>
                      {isCustom ? 'Tailored to your needs' : periodHint(p)}
                    </p>
                  </div>
                );
                return (
                  <PackageCardView
                    key={p.id}
                    pkg={p}
                    cardTestId={`plan-${p.name}`}
                    highlight={isCurrent}
                    renderPrice={renderPrice}
                    footer={
                      isCustom ? (
                        <button
                          type="button"
                          onClick={() => setContactPkg(p)}
                          data-testid={`contact-${p.name}`}
                          className="w-full px-3 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg transition-all"
                        >
                          Contact us
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCheckoutPkg(p)}
                          data-testid={`choose-${p.name}`}
                          className={`w-full px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                            isCurrent
                              ? 'bg-[#4ABDBD]/10 text-[#2D8A8A] hover:bg-[#4ABDBD]/20'
                              : 'text-white bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] hover:shadow-lg'
                          }`}
                        >
                          {isCurrent ? 'Renew' : 'Choose plan'}
                        </button>
                      )
                    }
                  />
                );
              })}
            </div>

            {loading && buyable.length === 0 && (
              <div className="py-8 flex justify-center" data-testid="plans-loading">
                <div className="h-6 w-6 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!loading && buyable.length === 0 && (
              <p className="text-sm text-slate-500">No plans available right now.</p>
            )}
          </div>
          )}

          {/* 3. Payment history + invoices (bottom) */}
          <div className="bg-white rounded-2xl shadow-md p-5 sm:p-6" data-testid="payment-history">
            <h3 className="text-base sm:text-lg font-bold text-[#1a365d] mb-3">Payment history</h3>
            {payments?.length > 0 ? (
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm whitespace-nowrap">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 border-b border-gray-100">
                      <th className="py-2 pr-3 font-semibold">Date</th>
                      <th className="py-2 pr-3 font-semibold">Plan</th>
                      <th className="py-2 pr-3 font-semibold text-right">Amount</th>
                      <th className="py-2 pr-3 font-semibold">Status</th>
                      <th className="py-2 font-semibold text-right">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                        <td className="py-2.5 pr-3 text-gray-600">{fmtDate(p.createdAt)}</td>
                        <td className="py-2.5 pr-3 text-gray-700 whitespace-normal">{p.packageName}</td>
                        <td className="py-2.5 pr-3 text-right font-semibold text-gray-800">
                          {p.amount && String(p.amount) !== '' ? `€${p.amount}` : '—'}
                          {Number(p.creditApplied) > 0 && (
                            <span className="block text-[10px] font-normal text-emerald-600">−€{p.creditApplied} credit</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3">
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            {String(p.status || 'paid').replace(/^\w/, (c) => c.toUpperCase())}
                          </span>
                        </td>
                        <td className="py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedInvoice(p)}
                            data-testid={`view-invoice-${p.id}`}
                            className="inline-flex items-center gap-1 text-[#4ABDBD] hover:text-[#2D8A8A] font-semibold"
                          >
                            <FileText className="w-3.5 h-3.5" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-500">No payments yet.</p>
            )}
          </div>
        </div>
      )}

      {checkoutPkg && (
        <SubscriptionCheckoutModal
          pkg={checkoutPkg}
          doctorId={doctorId}
          onClose={() => setCheckoutPkg(null)}
          onSuccess={refresh}
          initialPeriod={pendingPay && checkoutPkg.id === pendingPay.packageId ? pendingPay.period : null}
          lockPeriod={!!(pendingPay && checkoutPkg.id === pendingPay.packageId)}
        />
      )}

      {selectedInvoice && (
        <InvoiceModal
          payment={selectedInvoice}
          doctorId={doctorId}
          onClose={() => setSelectedInvoice(null)}
        />
      )}

      {contactPkg && (
        <ContactModal
          pkg={contactPkg}
          source="doctor"
          doctorId={doctorId}
          onClose={() => setContactPkg(null)}
        />
      )}
    </div>
  );
};

export default MySubscription;
